/// <reference lib="dom" />
/** @jsx h */
/** @jsxFrag Fragment */
import { Fragment, h, type VNode } from "preact";
import type { Param, ParamDict } from "@utils/params";
import { clamp } from "@utils/utils";
import { DatParamsFlat } from "@uiw/dat";
import { DatGraph } from "./datGraph.tsx";
import type { AdsrEnvelope, AdsrRamp } from "@wbaudio/utils";
import { AdsrEditor, AdsrInline } from "../chart/domains/time/adsrGraph.tsx";
import { FilterCurve } from "../chart/domains/frequency/filterCurve.tsx";
import { MoogFilterCurve } from "../chart/domains/frequency/moogFilterCurve.tsx";
import { PhaserCurve } from "../chart/domains/frequency/phaserCurve.tsx";
import { TelephonizerCurve } from "../chart/domains/frequency/telephonizerCurve.tsx";
import { WahWahCurve } from "../chart/domains/frequency/wahWahCurve.tsx";
import { CompressorTransferCurve } from "../chart/domains/transfer/compressorCurve.tsx";
import { WaveshaperTransferCurve } from "../chart/domains/transfer/waveshaperCurve.tsx";
import { LfoRateDepthCurve } from "../chart/domains/lfo/lfoRateDepthCurve.tsx";
import { TremoloCurve } from "../chart/domains/lfo/tremoloCurve.tsx";
import { DelayEchoGraph, PingPongEchoGraph } from "../chart/domains/time/echoGraphs.tsx";
import { GainBarGraph } from "../chart/domains/level/gainBarGraph.tsx";
import { PannerGraph } from "../chart/domains/level/pannerGraph.tsx";
import { BitcrusherGraph } from "../chart/domains/time/bitcrusherGraph.tsx";

export interface ParamGraphProps {
  params: ParamDict;
  onChange: (patch: Record<string, unknown>) => void;
}

export interface ParamGraph {
  label: string;
  keys: string[];
  Inline: (props: ParamGraphProps) => VNode;
  Editor?: (props: ParamGraphProps) => VNode;
}

const num = (params: ParamDict, key: string) => params[key]?.value as number;

const range = (param: Param | undefined) => ({
  min: (param as { min?: number } | undefined)?.min ?? 0,
  max: (param as { max?: number } | undefined)?.max ?? 1,
});

const axis = (params: ParamDict, key: string) => ({
  value: num(params, key),
  ...range(params[key]),
});

const bound = (props: ParamGraphProps, key: string, value: number) => {
  const { min, max } = range(props.params[key]);
  return clamp(value, min, max);
};

const set = (props: ParamGraphProps, patch: Record<string, unknown>) =>
  props.onChange(
    Object.fromEntries(
      Object.entries(patch).map(([k, v]) => [k, typeof v === "number" ? bound(props, k, v) : v]),
    ),
  );

const batched = (props: ParamGraphProps) => {
  let pending: Record<string, unknown> | null = null;
  return (key: string, value: unknown) => {
    if (!pending) {
      pending = {};
      queueMicrotask(() => {
        const patch = pending!;
        pending = null;
        set(props, patch);
      });
    }
    pending[key] = value;
  };
};

//#region filter
export interface FilterGraphKeys {
  frequency: string;
  q?: string;
  type?: string;
  filterType?: BiquadFilterType;
  extra?: string[];
}

export function FilterGraph(label: string, keys: FilterGraphKeys): ParamGraph {
  const view = (props: ParamGraphProps, editable: boolean) => (
    <FilterCurve
      frequency={num(props.params, keys.frequency)}
      q={keys.q ? num(props.params, keys.q) : Math.SQRT1_2}
      filterType={keys.type ? String(props.params[keys.type].value) : keys.filterType ?? "lowpass"}
      onDrag={editable
        ? (patch) =>
          set(props, {
            [keys.frequency]: patch.frequency,
            ...(keys.q ? { [keys.q]: patch.q } : {}),
          })
        : undefined}
    />
  );
  return {
    label,
    keys: [keys.frequency, keys.q, keys.type, ...(keys.extra ?? [])].filter((k): k is string =>
      !!k
    ),
    Inline: (props) => view(props, false),
    Editor: (props) => view(props, true),
  };
}
//#endregion

//#region adsr
const ToRamp = (
  ramp: unknown,
): AdsrRamp => (String(ramp).startsWith("exp") ? "exponential" : "linear");
const FromRamp = (ramp: AdsrRamp) => (ramp === "exponential" ? "exp" : "linear");

export function AdsrParamsToEnvelope(params: ParamDict, prefix: string): AdsrEnvelope {
  const get = (key: string) => params[`${prefix}${key}`]?.value;
  const at = num(params, `${prefix}attack/t`);
  const dt = num(params, `${prefix}decay/t`);
  const st = num(params, `${prefix}sustain/t`);
  const rt = num(params, `${prefix}release/t`);
  return {
    maxTime: at + dt + st + rt,
    attack: { t: at, v: get("attack/value") as number, ramp: ToRamp(get("attack/ramp")) },
    decay: { t: at + dt, v: get("decay/value") as number, ramp: ToRamp(get("decay/ramp")) },
    sustain: {
      t: at + dt + st,
      v: get("sustain/value") as number,
      ramp: ToRamp(get("sustain/ramp")),
    },
    release: { t: at + dt + st + rt, ramp: ToRamp(get("release/ramp")) },
  };
}

export function EnvelopeToAdsrParams(env: AdsrEnvelope, prefix: string): Record<string, unknown> {
  return {
    [`${prefix}attack/t`]: env.attack.t,
    [`${prefix}attack/value`]: env.attack.v,
    [`${prefix}attack/ramp`]: FromRamp(env.attack.ramp),
    [`${prefix}decay/t`]: env.decay.t - env.attack.t,
    [`${prefix}decay/value`]: env.decay.v,
    [`${prefix}decay/ramp`]: FromRamp(env.decay.ramp),
    [`${prefix}sustain/t`]: env.sustain.t - env.decay.t,
    [`${prefix}sustain/value`]: env.sustain.v,
    [`${prefix}sustain/ramp`]: FromRamp(env.sustain.ramp ?? "linear"),
    [`${prefix}release/t`]: env.release.t - env.sustain.t,
    [`${prefix}release/ramp`]: FromRamp(env.release.ramp),
  };
}

export function AdsrGraph(label: string, prefix: string): ParamGraph {
  const keys = Object.keys(EnvelopeToAdsrParams(AdsrParamsToEnvelope({}, prefix), prefix));
  return {
    label,
    keys,
    Inline: (props) => <AdsrInline value={AdsrParamsToEnvelope(props.params, prefix)} />,
    Editor: (props) => (
      <AdsrEditor
        value={AdsrParamsToEnvelope(props.params, prefix)}
        onChange={(env) => {
          const next = EnvelopeToAdsrParams(env, prefix);
          set(
            props,
            Object.fromEntries(
              keys.filter((k) => next[k] !== props.params[k]?.value).map((k) => [
                k,
                next[k],
              ]),
            ),
          );
        }}
      />
    ),
  };
}
//#endregion

//#region effects
export function CompressorGraph(): ParamGraph {
  const view = (props: ParamGraphProps, editable: boolean) => (
    <CompressorTransferCurve
      threshold={axis(props.params, "threshold")}
      ratio={num(props.params, "ratio")}
      knee={num(props.params, "knee")}
      onDragThreshold={editable ? (v) => set(props, { threshold: v }) : undefined}
    />
  );
  return {
    label: "transfer",
    keys: ["threshold", "ratio", "knee"],
    Inline: (props) => view(props, false),
    Editor: (props) => view(props, true),
  };
}

export function WaveshaperGraph(
  transfer?: (params: ParamDict) => (inputNorm: number) => number,
): ParamGraph {
  const view = (props: ParamGraphProps, editable: boolean) => (
    <WaveshaperTransferCurve
      drive={axis(props.params, "drive")}
      curveAmount={num(props.params, "curveAmount")}
      transfer={transfer?.(props.params)}
      onDragDrive={editable ? (v) => set(props, { drive: v }) : undefined}
    />
  );
  return {
    label: "transfer",
    keys: ["drive", "curveAmount", "algorithmIndex"],
    Inline: (props) => view(props, false),
    Editor: (props) => view(props, true),
  };
}

export function TelephonizerGraph(): ParamGraph {
  const view = (props: ParamGraphProps, editable: boolean) => (
    <TelephonizerCurve
      highpass={axis(props.params, "highpass")}
      lowpass={axis(props.params, "lowpass")}
      onChange={editable
        ? (v) => set(props, { highpass: v.highpass, lowpass: v.lowpass })
        : undefined}
    />
  );
  return {
    label: "band",
    keys: ["highpass", "lowpass"],
    Inline: (props) => view(props, false),
    Editor: (props) => view(props, true),
  };
}

export function TremoloGraph(): ParamGraph {
  const view = (props: ParamGraphProps, editable: boolean) => (
    <TremoloCurve
      params={props.params}
      rateParam={range(props.params.rate)}
      intensityParam={range(props.params.intensity)}
      onChange={editable ? batched(props) : undefined}
    />
  );
  return {
    label: "lfo",
    keys: ["rate", "intensity", "stereoPhase"],
    Inline: (props) => view(props, false),
    Editor: (props) => view(props, true),
  };
}

export function LfoGraph(rate = "rate", depth = "depth"): ParamGraph {
  const view = (props: ParamGraphProps, editable: boolean) => (
    <LfoRateDepthCurve
      rate={axis(props.params, rate)}
      depth={axis(props.params, depth)}
      onDrag={editable ? (v) => set(props, { [rate]: v.rate, [depth]: v.depth }) : undefined}
    />
  );
  return {
    label: "lfo",
    keys: [rate, depth],
    Inline: (props) => view(props, false),
    Editor: (props) => view(props, true),
  };
}

export function PhaserGraph(): ParamGraph {
  const view = (props: ParamGraphProps, editable: boolean) => (
    <PhaserCurve
      params={props.params}
      baseModulationFrequencyParam={range(props.params.baseModulationFrequency)}
      onChange={editable ? batched(props) : undefined}
    />
  );
  return {
    label: "notch",
    keys: ["baseModulationFrequency", "feedback"],
    Inline: (props) => view(props, false),
    Editor: (props) => view(props, true),
  };
}

export function MoogFilterGraph(): ParamGraph {
  const view = (props: ParamGraphProps, editable: boolean) => (
    <MoogFilterCurve
      params={props.params}
      cutoffParam={range(props.params.cutoff)}
      resonanceParam={range(props.params.resonance)}
      onChange={editable ? batched(props) : undefined}
    />
  );
  return {
    label: "filter",
    keys: ["cutoff", "resonance"],
    Inline: (props) => view(props, false),
    Editor: (props) => view(props, true),
  };
}

export function WahWahGraph(): ParamGraph {
  const view = (props: ParamGraphProps, editable: boolean) => (
    <WahWahCurve
      params={props.params}
      baseFrequencyParam={range(props.params.baseFrequency)}
      excursionOctavesParam={range(props.params.excursionOctaves)}
      onChange={editable ? batched(props) : undefined}
    />
  );
  return {
    label: "sweep",
    keys: ["baseFrequency", "excursionOctaves", "sweep"],
    Inline: (props) => view(props, false),
    Editor: (props) => view(props, true),
  };
}

export function DelayGraph(maxTime = 1000): ParamGraph {
  return {
    label: "echoes",
    keys: ["delayTime", "feedback"],
    Inline: (props) => (
      <DelayEchoGraph
        delayTime={num(props.params, "delayTime")}
        feedback={num(props.params, "feedback")}
        maxTime={maxTime}
      />
    ),
  };
}

export function PingPongGraph(maxTime = 2000): ParamGraph {
  return {
    label: "echoes",
    keys: ["delayTimeLeft", "delayTimeRight", "feedback"],
    Inline: (props) => (
      <PingPongEchoGraph
        delayTimeLeft={num(props.params, "delayTimeLeft")}
        delayTimeRight={num(props.params, "delayTimeRight")}
        feedback={num(props.params, "feedback")}
        maxTime={maxTime}
      />
    ),
  };
}

export function GainGraph(key = "gain"): ParamGraph {
  return {
    label: key,
    keys: [key],
    Inline: (props) => (
      <GainBarGraph gain={num(props.params, key)} gainMax={range(props.params[key]).max} />
    ),
  };
}

export function PanGraph(key = "pan"): ParamGraph {
  return {
    label: key,
    keys: [key],
    Inline: (props) => <PannerGraph pan={num(props.params, key)} />,
  };
}

export function CrushGraph(): ParamGraph {
  return {
    label: "steps",
    keys: ["bits", "normfreq"],
    Inline: (props) => (
      <BitcrusherGraph bits={num(props.params, "bits")} normfreq={num(props.params, "normfreq")} />
    ),
  };
}
//#endregion

//#region dat
const pick = (params: ParamDict, keys: string[]) =>
  Object.fromEntries(keys.filter((k) => params[k]).map((k) => [k, params[k]])) as ParamDict;

export interface DatParamGraphsProps {
  params: ParamDict;
  graphs?: ParamGraph[];
  onChange: (patch: Record<string, unknown>) => void;
}

export function DatParamGraphs(props: DatParamGraphsProps) {
  const graphs = (props.graphs ?? []).filter((g) => g.keys.every((k) => k in props.params));
  const covered = new Set(graphs.flatMap((g) => g.keys));
  const rest = Object.fromEntries(
    Object.entries(props.params).filter(([k]) => !covered.has(k)),
  ) as ParamDict;
  const graphProps = { params: props.params, onChange: props.onChange };
  const onParam = (key: string, value: unknown) => props.onChange({ [key]: value });

  return (
    <>
      {graphs.map((graph) => (
        <DatGraph
          key={graph.label + graph.keys[0]}
          label={graph.label}
          inline={<graph.Inline {...graphProps} />}
          editor={graph.Editor && <graph.Editor {...graphProps} />}
        >
          <DatParamsFlat params={pick(props.params, graph.keys)} onChange={onParam} />
        </DatGraph>
      ))}
      <DatParamsFlat params={rest} onChange={onParam} />
    </>
  );
}
//#endregion
